import * as React from 'react';

import { useQuery } from '@apollo/client';
import { sortBy } from 'lodash';

import { useLocation, useNavigate, useParams } from 'js/lib/useRouter';

import { useThirdPartyOrganization } from 'bundles/admin-dashboard/components/ThirdPartyOrganizationProvider';
import { getRecommendationItemTypeOptions } from 'bundles/enterprise-admin-ai-program-builder/components/ItemTypeMultiSelect';
import { useProgram } from 'bundles/enterprise-admin-ai-program-builder/hooks/useProgram';
import { useProgramGenerationRequestQuery } from 'bundles/enterprise-admin-ai-program-builder/hooks/useProgramGenerationRequestQuery';
import { useProgramSuggestionsQuery } from 'bundles/enterprise-admin-ai-program-builder/hooks/useProgramSuggestionsQuery';
import query from 'bundles/enterprise-admin-ai-program-builder/queries/AiProgramBuilderContext.graphql';
import type {
  AiProgramBuilderContextQuery,
  AiProgramBuilderContextQueryVariables,
} from 'bundles/enterprise-admin-ai-program-builder/queries/__generated__/AiProgramBuilderContext';
import type {
  AssemblerAiProgramBuilderContext,
  AssemblerAiProgramBuilderContextVariables,
} from 'bundles/enterprise-admin-ai-program-builder/queries/__generated__/AssemblerAiProgramBuilderContext';
import { assemblerQueryForAiProgramBuilderContext } from 'bundles/enterprise-admin-ai-program-builder/queries/assembler';
import type {
  ContentFocus,
  ContentSuggestion,
  Contract,
  GenerationStatus,
  InitialInput,
  RecommendationItemType,
  RecommendationType,
} from 'bundles/enterprise-admin-ai-program-builder/types';
import { shouldDisplayContract } from 'bundles/enterprise-admin-program-creation/utils/contract';
import { useWarningAndRedirectOnPageReload } from 'bundles/enterprise-ui/hooks';

const DEFAULT_LANGUAGE = 'en-US';
const DEFAULT_RECOMMENDATION_TYPE = 'COLLECTIONS';

type UploadedFile = {
  name: string;
  key: string;
  bucket: string;
};

type Type = {
  initialInput?: InitialInput;
  onInitialInputChange: React.Dispatch<React.SetStateAction<InitialInput | undefined>>;
  generationStatus: GenerationStatus;
  onGenerationStatusChange: React.Dispatch<React.SetStateAction<GenerationStatus>>;
  file?: UploadedFile;
  onFileChange: React.Dispatch<React.SetStateAction<UploadedFile | undefined>>;
  prompt: string;
  onPromptChange: React.Dispatch<React.SetStateAction<string>>;
  recommendationType: RecommendationType;
  onRecommendationTypeChange: React.Dispatch<React.SetStateAction<RecommendationType>>;
  contractOptions?: Contract[];
  contractId?: string;
  onContractIdChange: React.Dispatch<React.SetStateAction<string | undefined>>;
  programLanguage: string;
  onProgramLanguageChange: React.Dispatch<React.SetStateAction<string>>;
  contentFocus?: ContentFocus;
  onContentFocusChange: React.Dispatch<React.SetStateAction<ContentFocus | undefined>>;
  skills?: string[];
  onSkillsChange: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  roles?: string[];
  onRolesChange: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  focusItems?: string[];
  onFocusItemsChange: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  itemTypes?: RecommendationItemType[];
  onItemTypesChange: React.Dispatch<React.SetStateAction<RecommendationItemType[] | undefined>>;
  programName?: string;
  programNameError?: string;
  onProgramNameChange: React.Dispatch<React.SetStateAction<string | undefined>>;
  onProgramNameErrorChange: React.Dispatch<React.SetStateAction<string | undefined>>;
  programDescription?: string;
  programDescriptionError?: string;
  onProgramDescriptionChange: React.Dispatch<React.SetStateAction<string | undefined>>;
  onProgramDescriptionErrorChange: React.Dispatch<React.SetStateAction<string | undefined>>;
  generatedSuggestions: ContentSuggestion[];
  onGeneratedSuggestionsChange: React.Dispatch<React.SetStateAction<ContentSuggestion[]>>;
  generatedSuggestionsRemoved: number;
  onGeneratedSuggestionsRemovedChange: React.Dispatch<React.SetStateAction<number>>;
};

const AiProgramBuilderContext = React.createContext<Type>({
  onInitialInputChange: () => {},
  generationStatus: 'NOT_STARTED',
  onGenerationStatusChange: () => {},
  file: undefined,
  onFileChange: () => {},
  prompt: '',
  onPromptChange: () => {},
  recommendationType: 'COLLECTIONS',
  onRecommendationTypeChange: () => {},
  onContractIdChange: () => {},
  programLanguage: 'en_US',
  onProgramLanguageChange: () => {},
  onContentFocusChange: () => {},
  onSkillsChange: () => {},
  onRolesChange: () => {},
  onFocusItemsChange: () => {},
  onItemTypesChange: () => {},
  onProgramNameChange: () => {},
  onProgramNameErrorChange: () => {},
  onProgramDescriptionChange: () => {},
  onProgramDescriptionErrorChange: () => {},
  generatedSuggestions: [],
  onGeneratedSuggestionsChange: () => {},
  generatedSuggestionsRemoved: 0,
  onGeneratedSuggestionsRemovedChange: () => {},
});

export const useAiProgramBuilderContext = () => {
  return React.useContext(AiProgramBuilderContext);
};

const AiProgramBuilderContextProvider: React.LegacyFunctionComponentWithChildren<{
  initialInput?: Type['initialInput'];
  onInitialInputChange?: Type['onInitialInputChange'];
  file?: Type['file'];
  onFileChange?: Type['onFileChange'];
  onRecommendationTypeChange?: Type['onRecommendationTypeChange'];
  contractOptions?: Type['contractOptions'];
  onContractIdChange?: Type['onContractIdChange'];
  contractId?: Type['contractId'];
  contentFocus?: Type['contentFocus'];
  generationStatus?: Type['generationStatus'];
  generatedSuggestions?: Type['generatedSuggestions'];
}> = ({ children, ...mocks }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [initialInput, onInitialInputChange] = React.useState<InitialInput | undefined>();
  const [generationStatus, onGenerationStatusChange] = React.useState<GenerationStatus>(
    mocks.generationStatus ?? 'NOT_STARTED'
  );
  const [file, onFileChange] = React.useState<UploadedFile | undefined>(undefined);
  const [prompt, onPromptChange] = React.useState('');
  const [recommendationType, onRecommendationTypeChange] =
    React.useState<RecommendationType>(DEFAULT_RECOMMENDATION_TYPE);
  const [contractId, onContractIdChange] = React.useState<string | undefined>(undefined);
  const [programLanguage, onProgramLanguageChange] = React.useState<string>(DEFAULT_LANGUAGE);
  const [contentFocus, onContentFocusChange] = React.useState<ContentFocus | undefined>(undefined);
  const [skills, onSkillsChange] = React.useState<string[] | undefined>(undefined);
  const [roles, onRolesChange] = React.useState<string[] | undefined>(undefined);
  const [focusItems, onFocusItemsChange] = React.useState<string[] | undefined>(undefined);
  const [itemTypes, onItemTypesChange] = React.useState<RecommendationItemType[] | undefined>(
    getRecommendationItemTypeOptions(recommendationType)
  );
  const [programName, onProgramNameChange] = React.useState<string | undefined>(undefined);
  const [programNameError, onProgramNameErrorChange] = React.useState<string | undefined>(undefined);
  const [programDescription, onProgramDescriptionChange] = React.useState<string | undefined>(undefined);
  const [programDescriptionError, onProgramDescriptionErrorChange] = React.useState<string | undefined>(undefined);
  const [generatedSuggestions, onGeneratedSuggestionsChange] = React.useState<ContentSuggestion[]>([]);
  const [generatedSuggestionsRemoved, onGeneratedSuggestionsRemovedChange] = React.useState<number>(0);
  const thirdPartyOrganization = useThirdPartyOrganization();
  const hasMockedProps = Boolean(
    mocks.initialInput || mocks.file || mocks.contractOptions || mocks.contractId || mocks.generationStatus
  );
  const { data: gatewayData } = useQuery<AiProgramBuilderContextQuery, AiProgramBuilderContextQueryVariables>(query, {
    variables: {
      thirdPartyOrganizationId: thirdPartyOrganization.id,
      limit: 100,
    },
    context: { clientName: 'gatewayGql' },
    skip: hasMockedProps,
  });
  const { data: assemblerData } = useQuery<AssemblerAiProgramBuilderContext, AssemblerAiProgramBuilderContextVariables>(
    assemblerQueryForAiProgramBuilderContext,
    {
      variables: {
        orgId: thirdPartyOrganization.id,
      },
      skip: hasMockedProps,
    }
  );
  const contractOptions = React.useMemo(() => {
    const contracts = gatewayData?.EnterpriseContract?.queryContractsByOrgId.elements;
    const rosters = assemblerData?.EnterpriseUserRostersV1Resource?.byOrganization?.elements;
    const combinedContracts = contracts?.map((contract) => {
      return {
        id: contract.id,
        name: String(contract.name),
        contractState: contract.contractState,
        isReal: contract.isReal,
        limitType: contract.limitType,
        startsAt: contract.startsAt ?? undefined,
        endsAt: contract.endsAt ?? undefined,
        remainingUserLicense: contract.utilization.remainingUserLicense ?? undefined,
        remainingEnrollment: contract.utilization.remainingEnrollment ?? undefined,
        totalUserLicense: contract.utilization.totalUserLicense ?? undefined,
        totalEnrollment: contract.utilization.totalEnrollment ?? undefined,
        pendingInvitations:
          rosters?.reduce(
            (sum, roster) =>
              roster?.contract?.id === contract.id ? (roster.programInvitations?.paging.total ?? 0) + sum : sum,
            0
          ) ?? 0,
      };
    });
    const filtredContracts = combinedContracts?.filter(shouldDisplayContract);
    const sortedContracts = filtredContracts
      ? sortBy(filtredContracts, (contract) => contract?.name || contract?.id)
      : undefined;
    return sortedContracts;
  }, [gatewayData, assemblerData]);

  React.useEffect(() => {
    if (contractOptions && contractOptions.length === 1) {
      onContractIdChange(contractOptions[0].id);
    }
  }, [contractOptions]);

  const { thirdPartySlug, programDraftSlug } = useParams();
  const program = useProgram();
  const { programGenerationRequest, refetch: refetchGenerationRequest } = useProgramGenerationRequestQuery();
  useWarningAndRedirectOnPageReload(
    `/o/${thirdPartySlug}/program-builder/new-program`,
    initialInput,
    Boolean(programDraftSlug)
  );

  const { suggestions, refetch: refetchProgramSuggestions } = useProgramSuggestionsQuery(
    generationStatus !== 'COMPLETED',
    recommendationType
  );
  React.useEffect(() => {
    if (suggestions) {
      onGeneratedSuggestionsChange(suggestions);
      onGeneratedSuggestionsRemovedChange(0);
    }
  }, [suggestions]);

  React.useEffect(() => {
    if (programGenerationRequest) {
      onGenerationStatusChange(programGenerationRequest.status);
      if (programGenerationRequest.language) {
        onProgramLanguageChange(programGenerationRequest.language);
      }
      if (programGenerationRequest.files?.[0]) {
        onInitialInputChange('FILE');
        const path = programGenerationRequest.files[0];
        onFileChange({
          name: String(path.split('/').pop()),
          key: path,
          bucket: 'coursera-enterprise',
        });
      } else if (programGenerationRequest.prompt) {
        onInitialInputChange('PROMPT');
        onPromptChange(programGenerationRequest.prompt);
      }
      if (programGenerationRequest.curationType) {
        onRecommendationTypeChange(programGenerationRequest.curationType);
      }
      if (programGenerationRequest.curationItemTypes) {
        onItemTypesChange(programGenerationRequest.curationItemTypes);
      }
      if (programGenerationRequest.curationOrganization) {
        onContentFocusChange(programGenerationRequest.curationOrganization);
      } else {
        onContentFocusChange('SKILLS');
      }
      if (programGenerationRequest.skills) {
        onSkillsChange(programGenerationRequest.skills);
        if (
          programGenerationRequest.curationOrganization === 'SKILLS' ||
          !programGenerationRequest.curationOrganization
        ) {
          onFocusItemsChange(programGenerationRequest.skills);
        }
      }
      if (programGenerationRequest.roles) {
        onRolesChange(programGenerationRequest.roles);
        if (programGenerationRequest.curationOrganization === 'ROLES') {
          onFocusItemsChange(programGenerationRequest.roles);
        }
      }
    }
  }, [programGenerationRequest]);

  React.useEffect(() => {
    if (program && !programName) {
      onProgramNameChange(program.metadata.name);
      onProgramDescriptionChange(program.metadata.tagline ?? undefined);
    }
  }, [program, programName]);

  React.useEffect(() => {
    if (generationStatus === 'GENERATING_SUGGESTIONS' && program) {
      // Poll for the generation status every 5 seconds
      const interval = setInterval(() => {
        refetchGenerationRequest();
      }, 5000);
      return () => clearInterval(interval);
    } else if (generationStatus === 'COMPLETED' && program) {
      refetchProgramSuggestions();
    }
    return () => {};
  }, [generationStatus, program, refetchGenerationRequest, refetchProgramSuggestions]);

  React.useEffect(() => {
    if (generationStatus === 'FAILED' && !location.pathname.endsWith('/failure')) {
      navigate(`/o/${thirdPartySlug}/program-builder/failure?ref=FAILED_GENERATION_STATUS`);
    }
  }, [generationStatus, location.pathname, navigate, thirdPartySlug]);

  return (
    <AiProgramBuilderContext.Provider
      value={{
        initialInput,
        onInitialInputChange,
        file,
        onFileChange,
        prompt,
        onPromptChange,
        recommendationType,
        onRecommendationTypeChange,
        contractOptions,
        contractId,
        onContractIdChange,
        programLanguage,
        onProgramLanguageChange,
        contentFocus,
        onContentFocusChange,
        skills,
        onSkillsChange,
        roles,
        onRolesChange,
        focusItems,
        onFocusItemsChange,
        itemTypes,
        onItemTypesChange,
        programName,
        onProgramNameChange,
        programNameError,
        onProgramNameErrorChange,
        programDescription,
        onProgramDescriptionChange,
        programDescriptionError,
        onProgramDescriptionErrorChange,
        onGenerationStatusChange,
        generatedSuggestions,
        onGeneratedSuggestionsChange,
        generatedSuggestionsRemoved,
        onGeneratedSuggestionsRemovedChange,
        ...mocks,
        generationStatus,
      }}
    >
      {children}
    </AiProgramBuilderContext.Provider>
  );
};

export default AiProgramBuilderContextProvider;
